import { useQuery } from '@tanstack/react-query';
import { useContext, useMemo } from 'react';

import { useAuth } from '@sine/auth-client';
import { useTeamSwitcher } from '@sine/sine-team-switcher';
import { useNotification } from '@sine/sine-ui-components';

import { DeviceContext } from '../../external/device';
import { QueryKeys } from '../../query.client';
import { parseServerError } from '../../services/notification/notification.helpers';
import featureService from './../../services/features/feature.service';

/**
 * Custom hook for accessing features
 *
 * Returns an object with:
 * - features: Complete features object with admin, global, and site-specific features
 * - combinedFeatures: Deduplicated array of admin and global features
 * - isLoading: Whether the features are currently being loaded
 * - error: Any error that occurred during loading
 *
 * The admin array contains features from features page
 * The global array contains features controlled by billing
 * The site array contains site-specific features with their siteId
 */
export default function useEnabledFeatures() {
  const { addNotification } = useNotification();

  const { deviceToken } = useContext(DeviceContext);
  const { authState } = useAuth();
  const { isLoading: teamsAreLoading, currentTeam, error: teamError } = useTeamSwitcher();

  const currentTeamId = currentTeam?.id;

  const canFetch = !!deviceToken && authState === 'loggedIn' && !teamsAreLoading && !teamError && !!currentTeamId;

  const {
    data: features,
    isLoading,
    error,
  } = useQuery({
    enabled: canFetch,
    queryKey: [{ entity: QueryKeys.TEAM_FEATURES, id: currentTeamId }],
    // `queryFn` will only be called if `enabled` is `true` via `currentTeamId` being truthy
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    queryFn: () => featureService.getEnabledTeamFeatures(currentTeamId!),
    initialData: {
      admin: [],
      global: [],
      site: [],
    },
    onError: (err: any) =>
      addNotification({
        heading: 'Error while fetching features',
        message: parseServerError(err),
        variant: 'danger',
      }),
  });

  // Combine admin and global features
  const combinedFeatures = useMemo(() => {
    return [...features.admin, ...features.global].filter((feature, index, self) => self.indexOf(feature) === index);
  }, [features]);

  return {
    features,
    combinedFeatures,
    isLoading,
    error,
  } as const;
}
